import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Ticket, Plus, Copy, RefreshCw, Loader2, Ban, CheckCircle2, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const STATE_META = {
  active:    { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', label: 'Active' },
  exhausted: { color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',       label: 'Exhausted' },
  expired:   { color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',           label: 'Expired' },
  revoked:   { color: 'bg-rose-500/15 text-rose-300 border-rose-500/30',           label: 'Revoked' },
};

function StateBadge({ state }) {
  const meta = STATE_META[state] || STATE_META.active;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.color}`}>{meta.label}</span>;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function SignupInvitesPage() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState({});
  const [resending, setResending] = useState({});
  const [form, setForm] = useState({ label: '', max_uses: 1, expires_in_days: 30, email_lock: '' });
  const [justCreated, setJustCreated] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listSignupInvites();
      setInvites(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Could not load invites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); else setLoading(false); }, [load, isSuperAdmin]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        label: form.label.trim(),
        max_uses: Math.max(1, parseInt(form.max_uses, 10) || 1),
        expires_in_days: Math.max(1, parseInt(form.expires_in_days, 10) || 30),
        email_lock: form.email_lock.trim() || undefined,
      };
      const inv = await api.createSignupInvite(payload);
      setJustCreated(inv);
      setForm({ label: '', max_uses: 1, expires_in_days: 30, email_lock: '' });
      await load();
      toast.success('Invite minted');
    } catch (err) {
      toast.error(err.message || 'Could not mint invite');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (token) => {
    setRevoking(r => ({ ...r, [token]: true }));
    try {
      await api.revokeSignupInvite(token);
      toast.success('Invite revoked');
      await load();
    } catch (err) {
      toast.error(err.message || 'Could not revoke');
    } finally {
      setRevoking(r => ({ ...r, [token]: false }));
    }
  };

  const handleResend = async (token) => {
    setResending(r => ({ ...r, [token]: true }));
    try {
      const res = await api.resendSignupInvite(token);
      toast.success(`Invite resent to ${res.to}`);
    } catch (err) {
      toast.error(err.message || 'Could not resend invite');
    } finally {
      setResending(r => ({ ...r, [token]: false }));
    }
  };

  const inviteUrl = (token) => `${window.location.origin}/register?invite=${token}`;

  const copyToClipboard = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Could not copy');
    }
  };

  const counts = invites.reduce((acc, i) => { acc[i.state] = (acc[i.state] || 0) + 1; return acc; }, {});

  if (!isSuperAdmin) {
    return (
      <div className="p-8" data-testid="signup-invites-forbidden">
        <div className="max-w-md mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-white mb-1">Super admin only</h2>
          <p className="text-sm text-zinc-300">Only super admins can mint signup invites.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="signup-invites-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white flex items-center gap-2">
            <Ticket className="w-6 h-6 text-blue-400" />
            Signup Invites
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Mint invite codes to let prospects self-register and start a 14-day trial.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="refresh-invites-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="create-invite-btn">
            <Plus className="w-4 h-4 mr-1" /> New Invite
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['active', 'exhausted', 'expired', 'revoked'].map(s => (
          <div key={s} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">{STATE_META[s].label}</div>
            <div className="text-2xl font-semibold text-white mt-1" data-testid={`count-${s}`}>{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-zinc-400" data-testid="invites-loading">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />Loading…
          </div>
        ) : invites.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="invites-empty">
            No invites yet. Mint one to share a `/register?invite=…` link with a prospect.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Label</th>
                <th className="text-left px-4 py-3">State</th>
                <th className="text-left px-4 py-3">Uses</th>
                <th className="text-left px-4 py-3">Email lock</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {invites.map(inv => (
                <tr key={inv.token} className="hover:bg-white/[0.02]" data-testid={`invite-row-${inv.token}`}>
                  <td className="px-4 py-3 text-white">{inv.label || <span className="text-zinc-500">(no label)</span>}</td>
                  <td className="px-4 py-3"><StateBadge state={inv.state} /></td>
                  <td className="px-4 py-3 text-zinc-300">{inv.used_count} / {inv.max_uses}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{inv.email_lock || '—'}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtTime(inv.expires_at)}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{fmtTime(inv.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(inviteUrl(inv.token), 'Invite link copied')}
                        className="rounded-lg bg-white/5 border-white/10 text-zinc-300 h-7 px-2"
                        data-testid={`copy-link-${inv.token}`}
                        title="Copy invite link"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      {inv.state === 'active' && inv.email_lock && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResend(inv.token)}
                          disabled={resending[inv.token]}
                          className="rounded-lg bg-blue-500/10 border-blue-500/30 text-blue-300 h-7 px-2 hover:bg-blue-500/20"
                          data-testid={`resend-invite-${inv.token}`}
                          title="Resend branded invite email"
                        >
                          {resending[inv.token] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        </Button>
                      )}
                      {inv.state === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevoke(inv.token)}
                          disabled={revoking[inv.token]}
                          className="rounded-lg bg-rose-500/10 border-rose-500/30 text-rose-300 h-7 px-2 hover:bg-rose-500/20"
                          data-testid={`revoke-invite-${inv.token}`}
                          title="Revoke invite"
                        >
                          {revoking[inv.token] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setJustCreated(null); }}>
        <DialogContent className="bg-zinc-950 border-white/10 text-white sm:max-w-md" data-testid="create-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{justCreated ? 'Invite ready' : 'New signup invite'}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {justCreated ? 'Share this link with the prospect. It will pre-fill the invite code on the signup page.' : 'Configure a code prospects can redeem to create their tenant.'}
            </DialogDescription>
          </DialogHeader>

          {justCreated ? (
            <div className="space-y-3" data-testid="invite-just-created">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-emerald-200">Invite minted. Anyone with this link can register a new tenant ({justCreated.max_uses} use{justCreated.max_uses === 1 ? '' : 's'}, expires {fmtTime(justCreated.expires_at)}).</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs">Invite link</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={inviteUrl(justCreated.token)}
                    className="bg-white/5 border-white/10 text-white text-xs font-mono rounded-xl"
                    data-testid="created-invite-link"
                  />
                  <Button onClick={() => copyToClipboard(inviteUrl(justCreated.token), 'Invite link copied')} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="copy-created-link-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs">Raw token</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={justCreated.token}
                    className="bg-white/5 border-white/10 text-white text-xs font-mono rounded-xl"
                    data-testid="created-invite-token"
                  />
                  <Button onClick={() => copyToClipboard(justCreated.token, 'Token copied')} variant="outline" className="rounded-xl bg-white/5 border-white/10 text-zinc-300">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setCreateOpen(false); setJustCreated(null); }} className="rounded-xl bg-white/10 hover:bg-white/15 text-white" data-testid="close-created-btn">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs">Label</Label>
                <Input
                  data-testid="invite-label-input"
                  placeholder="e.g. Acme Corp demo"
                  value={form.label}
                  onChange={e => setForm({ ...form, label: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">Max uses</Label>
                  <Input
                    data-testid="invite-max-uses-input"
                    type="number"
                    min={1}
                    value={form.max_uses}
                    onChange={e => setForm({ ...form, max_uses: e.target.value })}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">Expires (days)</Label>
                  <Input
                    data-testid="invite-expires-input"
                    type="number"
                    min={1}
                    value={form.expires_in_days}
                    onChange={e => setForm({ ...form, expires_in_days: e.target.value })}
                    className="bg-white/5 border-white/10 text-white rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs">Email lock (optional)</Label>
                <Input
                  data-testid="invite-email-lock-input"
                  type="email"
                  placeholder="prospect@company.com"
                  value={form.email_lock}
                  onChange={e => setForm({ ...form, email_lock: e.target.value })}
                  className="bg-white/5 border-white/10 text-white rounded-xl"
                />
                <p className="text-[10px] text-zinc-500">If set, only this email can redeem the invite.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="cancel-invite-btn">Cancel</Button>
                <Button type="submit" disabled={creating} className="rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="submit-invite-btn">
                  {creating ? 'Minting…' : 'Mint invite'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
